import { useCallback } from 'react';

interface UseEventHandlersParams {
  setValue: (field: string, value: string, options?: { shouldValidate?: boolean }) => void;
  clearDependentFields: (fields: string[]) => void;
  fetchIntakesForUniversity: (universityId: string, feeStatus?: string) => void;
  fetchCoursesForIntake: (intakeId: string, feeStatus: string) => void;
  feeStatus: string;
  universityId: string;
}

export function useEventHandlers({
  setValue,
  clearDependentFields,
  fetchIntakesForUniversity,
  fetchCoursesForIntake,
  feeStatus,
  universityId,
}: UseEventHandlersParams) {
  // Event handlers
  const handleUniversityChange = useCallback(
    (evt: React.FormEvent<HTMLSelectElement>) => {
      const { value } = evt.currentTarget;
      setValue('universityId', value, { shouldValidate: true });
      clearDependentFields([
        'feeStatus',
        'intakeId',
        'studyLevel',
        'courseName',
        'mode',
        'campus',
        'entryPoint',
        'courseId',
      ]);
      // Don't fetch intakes here - wait for fee status selection
    },
    [setValue, clearDependentFields],
  );

  const handleFeeStatusChange = useCallback(
    (evt: React.FormEvent<HTMLSelectElement>) => {
      const { value } = evt.currentTarget;
      setValue('feeStatus', value, { shouldValidate: false });
      clearDependentFields(['intakeId', 'studyLevel', 'courseName', 'mode', 'campus', 'entryPoint', 'courseId']);
      // Fetch intakes filtered by fee status when fee status changes
      if (universityId && value) {
        fetchIntakesForUniversity(universityId, value);
      }
    },
    [setValue, clearDependentFields, fetchIntakesForUniversity, universityId],
  );

  const handleIntakeChange = useCallback(
    (evt: React.FormEvent<HTMLSelectElement>) => {
      const { value } = evt.currentTarget;
      setValue('intakeId', value, { shouldValidate: true });
      clearDependentFields(['studyLevel', 'courseName', 'mode', 'campus', 'entryPoint', 'courseId']);
      // Fetch courses only if both intake and fee status are selected
      if (value && feeStatus) {
        fetchCoursesForIntake(value, feeStatus);
      }
    },
    [setValue, clearDependentFields, fetchCoursesForIntake, feeStatus],
  );

  const handleStudyLevelChange = useCallback(
    (evt: React.FormEvent<HTMLSelectElement>) => {
      const { value } = evt.currentTarget;
      setValue('studyLevel', value, { shouldValidate: true });
      clearDependentFields(['courseName', 'mode', 'campus', 'entryPoint', 'courseId']);
    },
    [setValue, clearDependentFields],
  );

  // Course selection now sets the course name (aggregated courses)
  const handleCourseNameChange = useCallback(
    (value: string) => {
      setValue('courseName', value, { shouldValidate: true });
      clearDependentFields(['mode', 'campus', 'entryPoint', 'courseId']);
    },
    [setValue, clearDependentFields],
  );

  const handleModeChange = useCallback(
    (evt: React.FormEvent<HTMLSelectElement>) => {
      const { value } = evt.currentTarget;
      setValue('mode', value, { shouldValidate: true });
      clearDependentFields(['campus', 'entryPoint', 'courseId']);
    },
    [setValue, clearDependentFields],
  );

  const handleCampusChange = useCallback(
    (evt: React.FormEvent<HTMLSelectElement>) => {
      const { value } = evt.currentTarget;
      setValue('campus', value, { shouldValidate: true });
      clearDependentFields(['entryPoint', 'courseId']);
    },
    [setValue, clearDependentFields],
  );

  const handleEntryPointChange = useCallback(
    (evt: React.FormEvent<HTMLSelectElement>) => {
      const { value } = evt.currentTarget;
      setValue('entryPoint', value, { shouldValidate: true });
    },
    [setValue],
  );

  return {
    handleUniversityChange,
    handleFeeStatusChange,
    handleIntakeChange,
    handleStudyLevelChange,
    handleCourseNameChange,
    handleModeChange,
    handleCampusChange,
    handleEntryPointChange,
  };
}
